import {
  AcquisitionChannel,
  Currency,
  getMetricAggregateViewsByCriteria,
  LoanStatusExternal,
  MetricAggregateView,
  ProductExternal,
  PurchaseCountry,
} from '@nemuru-eng/js-api-client';
import { useState } from 'react';

import { CurrencyFilter } from '@/components/modules/Metrics/shared/entities/Filter/CurrencyFilter';
import { EndDateFilter } from '@/components/modules/Metrics/shared/entities/Filter/EndDateFilter';
import { LoanStatusExternalFilter } from '@/components/modules/Metrics/shared/entities/Filter/LoanStatusExternalFilter';
import { ProductExternalFilter } from '@/components/modules/Metrics/shared/entities/Filter/ProductExternalFilter';
import { PurchaseCountryFilter } from '@/components/modules/Metrics/shared/entities/Filter/PurchaseCountryFilter';
import { StartDateFilter } from '@/components/modules/Metrics/shared/entities/Filter/StartDateFilter';
import { QueryBuilder } from '@/components/modules/Metrics/shared/entities/QueryBuilder';
import { TimeInterval } from '@/components/modules/Metrics/shared/entities/TimeInterval';
import { useOperationsContext } from '@/contexts/OperationsContext/OperationsContext';

interface UseMetricAggregateViewsProps {
  currency: Currency;
  timeInterval: TimeInterval;
  loanStatusExternal: LoanStatusExternal[];
  productExternal?: ProductExternal[];
  purchaseCountries?: PurchaseCountry[];
  acquisitionChannel?: AcquisitionChannel[];
}

export const useMetricAggregateViews = ({
  currency,
  timeInterval,
  productExternal,
  purchaseCountries,
  loanStatusExternal,
}: UseMetricAggregateViewsProps) => {
  const {
    state: { metricAggregateQueryBuilder: queryBuilder, metricAggregateViews },
    actions: { updateOperationsContext },
  } = useOperationsContext();

  const [hasRequested, setHasRequested] = useState(false);
  const [filteredMetrics, setFilteredMetrics] = useState<MetricAggregateView[] | undefined>(undefined);

  const setQueryBuilder = (queryBuilder: QueryBuilder) =>
    updateOperationsContext({ metricAggregateQueryBuilder: queryBuilder });

  const getMetricAggregateViews = async (force = false) => {
    const newQueryBuilder = new QueryBuilder([
      new CurrencyFilter([currency]),
      new StartDateFilter([timeInterval]),
      new EndDateFilter([timeInterval]),
      new LoanStatusExternalFilter(loanStatusExternal),
      new PurchaseCountryFilter(purchaseCountries),
      new ProductExternalFilter(productExternal),
    ]);

    if (force || !queryBuilder || queryBuilder.needsRequest(newQueryBuilder)) {
      const query = newQueryBuilder.merge(queryBuilder).createQuery();
      // live refresh: keep the current figures on screen until the new ones arrive
      if (!force) setHasRequested(false);
      try {
        const newMetrics = await getMetricAggregateViewsByCriteria({ query });
        updateOperationsContext({ metricAggregateViews: newMetrics });
        const filteredMetrics = newQueryBuilder.filterMetrics(newMetrics);
        setFilteredMetrics(filteredMetrics);
        setQueryBuilder(newQueryBuilder.merge(queryBuilder));
      } catch (error) {}
    } else {
      if (metricAggregateViews) {
        const filteredMetrics = newQueryBuilder.filterMetrics(metricAggregateViews);
        setFilteredMetrics(filteredMetrics);
      }
    }
    setHasRequested(true);
  };

  return { getMetricAggregateViews, filteredMetrics, hasRequested };
};
